/**
 * Top-level error boundary.
 *
 * A render failure shows a recovery path instead of a blank page. The message the user sees is
 * generic on purpose — an exception message can carry internals — while the technical detail is
 * left in the browser console for a developer, and never sent anywhere.
 */
import { Component, type ErrorInfo, type ReactElement, type ReactNode } from 'react';

interface State {
  failed: boolean;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // Console only: no error-reporting service is configured, and shipping the component stack
    // to a third party could carry credential data.
    console.error('Beklenmeyen arayüz hatası', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return <BoundaryFallback onReset={() => this.setState({ failed: false })} />;
  }
}

function BoundaryFallback({ onReset }: { onReset: () => void }): ReactElement {
  return (
    <div className="login-wrap">
      <div className="login-card">
        <h1>Beklenmeyen bir hata oluştu</h1>
        <p className="small muted">
          Ekran yüklenirken bir sorun çıktı. Hiçbir kayıt değişmedi. Sayfayı yenilemek çoğu durumda
          yeterlidir.
        </p>
        <div className="row-gap">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              onReset();
              window.location.reload();
            }}
          >
            Sayfayı yenile
          </button>
          {/* `/admin` rather than `/`: the root is the public landing page now, and a control that
              says «back to the panel» has to arrive at the panel. */}
          <a className="btn btn-ghost" href="/admin">
            Panele dön
          </a>
        </div>
      </div>
    </div>
  );
}
